/* External */
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { injectIntl } from 'react-intl';
import { useHistory } from 'react-router';
import PropTypes from 'prop-types';
import classNames from 'classnames';

/* Components */
import DetailsFrame from '../../components/detailsFrame/NewDetailsFrame';
import GtmComponent from '../../components/GtmContext/GtmComponent';
import { gtmDimensions } from '../../components/GtmContext/dimensions/pdp';
import DetailsTab from '../../components/seriesDetails/detailsTab/DetailsTab';
import MoreLikeThis from '../moreLikeThis/MoreLikeThis';
import Metadata from '../metadata/Metadata';

/* Styles */
import './VodContent.scss';

/* Other */
import {
  formatSeconds,
  isMoreLikeThisActive,
} from '../../utils';
import { api } from '../../state/configureStore';
import { useIsResponsive } from '../../hooks/useIsResponsive';

/* Constants */
const MORE_LIKE_THIS_TAB_KEY = 'moreLikeThis';
const DETAILS_TAB_KEY = 'details';

function VodContent({
  assetId,
  intl: { formatMessage },
  isPublic = false,
  dataPublic = {},
}) {
  const {
    data = {},
    isError: isAssetError,
    isLoading: isAssetLoading,
    isSuccess: isAssetSuccess,
  } = isPublic ? dataPublic : api.useGetAssetDetailQuery({ assetId });
  const asset = data;
  const { heartbeatOnGoing } = useSelector((state) => state.vrioHeartbeat);
  const [selectedTab, setSelectedTab] = useState(
    isPublic || !isMoreLikeThisActive ? DETAILS_TAB_KEY : MORE_LIKE_THIS_TAB_KEY,
  );
  const {
    data: continueWatchingData = {}, isLoading: isContinueWatchingLoading,
  } = api.useGetContinueWatchingDataQuery(
    { assetId: asset?.vrioAssetId, assetType: 'movies', vmsId: asset?.vmsId },
    { skip: !isAssetSuccess || heartbeatOnGoing },
  );

  const isMobile = useIsResponsive();
  const history = useHistory();

  useEffect(() => {
    if (isAssetError) history.replace('/404');
  }, [history, isAssetError]);

  const isLoading = isAssetLoading || isContinueWatchingLoading;
  const { progress } = continueWatchingData;
  const {
    description, genres, rating, title, vod, vmsId, shouldDisplayLockIcon,
  } = asset;
  const { duration, releaseYear } = (isPublic) ? asset : vod || {};
  const formattedDuration = duration && formatSeconds(duration, 'minimal');
  const formattedLongDuration = duration && formatSeconds(duration, 'long');

  const genreMetadata = (rating || genres) && (
    <Metadata metadataValues={genres} rating={rating} showRating />
  );

  const durationMetadata = (releaseYear || duration) && (
    <Metadata metadataValues={[releaseYear, formattedDuration]} />
  );

  const progressOffset = useMemo(() => {
    if (progress >= duration * 0.95) {
      return 0;
    }

    return progress;
  }, [duration, progress]);

  function getIconButtonPlayer() {
    if (isPublic) {
      return 'dtv-icon-layers';
    }

    if (shouldDisplayLockIcon) {
      return 'dtv-icon-lock';
    }
    return 'dtv-icon-play';
  }

  function getFormatTextButton() {
    if (isPublic) {
      return formatMessage({
        id: 'public.pdp.seePlans',
        defaultMessage: 'Conocer planes',
      });
    }

    if (shouldDisplayLockIcon) {
      return formatMessage({
        id: 'public.pdp.playButton',
        defaultMessage: 'Quiero verlo',
      });
    }

    if (progressOffset > 0) {
      return formatMessage({
        id: 'vod.resume',
        defaultMessage: 'Reanudar',
      });
    }

    return formatMessage({
      id: 'interstitial.watchNow',
      defaultMessage: 'Ver ahora',
    });
  }

  const pdpPlayButtonIcon = getIconButtonPlayer();
  const pdpPlayButtonText = getFormatTextButton();

  const { description: assetDescription } = asset;

  const tabs = {
    ...((isMoreLikeThisActive && !isPublic) ? {
      [MORE_LIKE_THIS_TAB_KEY]: formatMessage({
        id: 'movie.moreLikeThis',
        defaultMessage: 'Contenidos Relacionados',
      }),
    } : {}),
    [DETAILS_TAB_KEY]: formatMessage({
      id: 'movie.details',
      defaultMessage: 'Detalles',
    }),
  };

  const hanleClickSeasonSelector = (tabKey) => {
    setSelectedTab(tabKey);
  };

  return (
    <div className="dtv-vod-content">
      {assetDescription && (
        <GtmComponent
          assetToPlay={asset}
          config={{
            playSource: 'pdp',
            transmissionType: 'movie',
            type: 'movie',
            contentPlayerType: 'movie',
          }}
          hash={gtmDimensions.pdp_vod.page_view.hash}
          eventType={gtmDimensions.pdp_vod.page_view.eventType}
          dimensions={gtmDimensions.pdp_vod.page_view.dimensions}
        />
      )}
      <DetailsFrame
        assetId={vmsId}
        assetToPlay={asset}
        description={description}
        fullHeight
        isContinueWatchingAsset={!!progressOffset}
        isLoading={isLoading}
        genreMetadata={genreMetadata}
        durationMetadata={durationMetadata}
        pdpPlayButtonIcon={pdpPlayButtonIcon}
        pdpPlayButtonText={pdpPlayButtonText}
        progress={progressOffset}
        showMyListButton
        title={title}
        isPublic={isPublic}
      />
      {
        (isMoreLikeThisActive && !isPublic) ? (
          <>
            <div className="dtv-series-details-tab-selector">
              {Object.keys(tabs).map((tabKey) => {
                const tabText = tabs[tabKey];

                return (
                  <span
                    className={classNames({
                      active: tabKey === selectedTab,
                    })}
                    key={tabKey}
                    onClick={() => hanleClickSeasonSelector(tabKey)}
                  >
                    {tabText}
                  </span>
                );
              })}
            </div>

            {selectedTab === MORE_LIKE_THIS_TAB_KEY && (
              <MoreLikeThis contentId={assetId} key={isMobile} />
            )}
            {selectedTab === DETAILS_TAB_KEY && (
              <DetailsTab asset={asset} duration={formattedLongDuration} isLoading={isLoading} />
            )}
          </>
        ) : (
          <>
            <div className="dtv-series-details-tab-selector">
              {Object.keys(tabs).map((tabKey) => {
                const tabText = tabs[tabKey];

                return (
                  <span
                    className={classNames({
                      active: tabKey === selectedTab,
                    })}
                    key={tabKey}
                    onClick={() => hanleClickSeasonSelector(tabKey)}
                  >
                    {tabText}
                  </span>
                );
              })}
            </div>
            <DetailsTab asset={asset} duration={formattedLongDuration} isLoading={isLoading} />
          </>
        )
      }
    </div>
  );
}

VodContent.propTypes = {
  assetId: PropTypes.string.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
  isPublic: PropTypes.bool,
  dataPublic: PropTypes.shape({}),
};

VodContent.defaultProps = {
  isPublic: false,
  dataPublic: {},
};

export default injectIntl(VodContent);
